import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Activity, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/channels" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { await login(email, password); } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-[#070A0F]">
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-slate-800 mcr-grid-bg relative overflow-hidden">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded bg-cyan-500/15 border border-cyan-500/40 grid place-items-center"><Activity size={16} className="text-cyan-400" /></div>
          <span className="font-display font-semibold text-slate-100">MediaCore Origin</span>
        </div>
        <div className="max-w-md fade-up">
          <div className="text-[11px] font-mono text-cyan-400 tracking-[0.2em] mb-4">STANDALONE STREAM ORIGIN · SPEC V4</div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-slate-100 leading-[1.05]">One clean feed.<br />Whatever happens upstream.</h1>
          <p className="text-slate-400 mt-5 text-sm leading-relaxed">Five-source ladders, discontinuity-correct packaging, held frame and slate, provider connection limits, and IP-whitelisted outputs for your downstream servers.</p>
        </div>
        <div className="grid grid-cols-3 gap-3 font-mono text-[11px]">
          {[["HLS", "/live/<slug>/index.m3u8"], ["MPEG-TS", "/ts/<slug>.ts"], ["RTMP", "push ×3"]].map(([a, b]) => (
            <div key={a} className="border border-slate-800 rounded p-3 bg-[#0B111B]"><div className="text-emerald-400">{a}</div><div className="text-slate-500 mt-1 truncate">{b}</div></div>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5 fade-up" data-testid="login-form">
          <div>
            <div className="flex items-center gap-2 text-slate-400 text-xs font-mono mb-2"><Lock size={12} /> OPERATOR ACCESS</div>
            <h2 className="text-2xl font-semibold text-slate-100">Sign in to the console</h2>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Email</label>
            <Input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-[#0F1722] border-slate-700 font-mono" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Password</label>
            <Input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="bg-[#0F1722] border-slate-700 font-mono" />
          </div>
          {err && <div data-testid="login-error" className="text-sm text-red-400 border border-red-500/30 bg-red-500/10 rounded px-3 py-2">{err}</div>}
          <Button data-testid="login-submit-button" type="submit" disabled={busy} className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold">
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
